/**
 * 时区配置
 */

export interface TimezoneOption {
  value: string
  label: string
  offset: string // UTC偏移量
  region: string // 时区缩写
  group: 'asia' | 'europe' | 'america' // 地区分组
}

/**
 * 时区分组配置
 */
export const TIMEZONE_GROUPS = {
  asia: {
    label: '亚洲',
    emoji: '🌏',
  },
  europe: {
    label: '欧洲',
    emoji: '🌍',
  },
  america: {
    label: '美洲',
    emoji: '🌎',
  },
}

/**
 * 主要时区配置
 *
 * 关于夏令时（Daylight Saving Time, DST）说明：
 *
 * 美国时区：
 * - 格式：UTC-x/-y 表示 "标准时间/夏令时"
 * - 第一个值：标准时间的 UTC 偏移（通常在11月-3月）
 * - 第二个值：夏令时的 UTC 偏移（通常在3月-11月）
 * - 夏令时期间，时钟会向前调整1小时，所以 UTC 偏移值会减少1
 *
 * 欧洲时区：
 * - 英国：UTC+0（冬季）/ UTC+1（夏季）
 * - 中欧（法国、德国、意大利）：UTC+1（冬季）/ UTC+2（夏季）
 * - 欧洲夏令时通常在3月最后一个周日到10月最后一个周日
 *
 * 亚洲时区：
 * - 中国、日本不实行夏令时，全年固定偏移量
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // ========== 亚洲时区 ==========
  {
    value: 'Asia/Shanghai',
    label: '中国标准时间',
    offset: 'UTC+8', // 中国不实行夏令时
    region: 'CST', // China Standard Time
    group: 'asia',
  },
  {
    value: 'Asia/Tokyo',
    label: '日本标准时间',
    offset: 'UTC+9', // 日本不实行夏令时
    region: 'JST', // Japan Standard Time
    group: 'asia',
  },

  // ========== 欧洲时区 ==========
  {
    value: 'Europe/London',
    label: '英国时间',
    offset: 'UTC+0/+1', // GMT(冬)/BST(夏)
    region: 'GMT/BST', // Greenwich Mean Time/British Summer Time
    group: 'europe',
  },
  {
    value: 'Europe/Paris',
    label: '法国时间',
    offset: 'UTC+1/+2', // CET(冬)/CEST(夏)
    region: 'CET/CEST', // Central European Time/Central European Summer Time
    group: 'europe',
  },
  {
    value: 'Europe/Berlin',
    label: '德国时间',
    offset: 'UTC+1/+2', // CET(冬)/CEST(夏)
    region: 'CET/CEST', // Central European Time/Central European Summer Time
    group: 'europe',
  },
  {
    value: 'Europe/Rome',
    label: '意大利时间',
    offset: 'UTC+1/+2', // CET(冬)/CEST(夏)
    region: 'CET/CEST', // Central European Time/Central European Summer Time
    group: 'europe',
  },

  // ========== 美洲时区 ==========
  {
    value: 'America/New_York',
    label: '美国东部时间',
    offset: 'UTC-5/-4', // EST(冬)/EDT(夏)
    region: 'EST/EDT', // Eastern Standard/Daylight Time
    group: 'america',
  },
  {
    value: 'America/Chicago',
    label: '美国中部时间',
    offset: 'UTC-6/-5', // CST(冬)/CDT(夏)
    region: 'CST/CDT', // Central Standard/Daylight Time
    group: 'america',
  },
  {
    value: 'America/Denver',
    label: '美国山地时间',
    offset: 'UTC-7/-6', // MST(冬)/MDT(夏)
    region: 'MST/MDT', // Mountain Standard/Daylight Time
    group: 'america',
  },
  {
    value: 'America/Los_Angeles',
    label: '美国太平洋时间',
    offset: 'UTC-8/-7', // PST(冬)/PDT(夏)
    region: 'PST/PDT', // Pacific Standard/Daylight Time
    group: 'america',
  },
]

// 默认时区
export const DEFAULT_TIMEZONE = 'Asia/Shanghai'

// localStorage key
export const TIMEZONE_STORAGE_KEY = 'admin_timezone'
